import {
  HHChip,
  HHCollapsibleTable,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useState } from 'react';
import { PackageType } from '../../types';
import { calculateChipColor } from '../../utils/contract-utils';
import { IRevenueShare } from '../../utils/discount-utils';
import LoadingComponent from '../utils/loading';
import RevenueShareDetail from './revenue-share-detail';
import RevenueShareLevelDisplay from './revenue-share-level-display';

export interface RevenueSharesListProps {
  revenueSharesData?: IRevenueShare[];
  loading?: boolean;
  availablePackages: PackageType[];
  onEditRevenueShareClick?: (revenueShareDefinitionId: number) => void;
  // When both clientMap and partnershipMap are provided, the table switches to
  // a "scoped" layout showing Partnership and Client columns (used by the global
  // revenue shares grid). Without them it renders a simpler contract-level view.
  clientMap?: Map<number, string>;
  partnershipMap?: Map<number, string>;
}

const RevenueSharesList = ({
  revenueSharesData,
  loading = true,
  onEditRevenueShareClick,
  clientMap,
  partnershipMap,
}: RevenueSharesListProps): JSX.Element => {
  const [openRowId, setOpenRowId] = useState<number | null>(null);
  const showScopeColumns = !!(clientMap && partnershipMap);

  const handleOpenRow =
    (id: number): React.MouseEventHandler<HTMLButtonElement> =>
    (): void => {
      setOpenRowId(openRowId !== id ? id : null);
    };

  const headers = showScopeColumns
    ? [
        { align: 'left' as const, headerTitle: 'Details' },
        { align: 'left' as const, headerTitle: 'Partnership' },
        { align: 'left' as const, headerTitle: 'Client' },
        { align: 'left' as const, headerTitle: 'Effective Year' },
        { align: 'left' as const, headerTitle: 'Status' },
      ]
    : [
        { align: 'left' as const, headerTitle: 'Type' },
        { align: 'left' as const, headerTitle: 'Details' },
        { align: 'left' as const, headerTitle: 'Effective Year' },
        { align: 'left' as const, headerTitle: 'Status' },
      ];

  return (
    <HHCollapsibleTable
      headers={headers}
      emptyRowContent={
        loading ? (
          <LoadingComponent center />
        ) : (
          <HHStack direction="row" padding={4} justifyContent="center">
            <HHTypography hhVariant="muted">
              No associated revenue shares to display
            </HHTypography>
          </HHStack>
        )
      }
      rows={
        revenueSharesData
          ? revenueSharesData.map(revShare => ({
              open: openRowId === revShare.revenueShareDefinitionId,
              setOpen: handleOpenRow(revShare.revenueShareDefinitionId),
              rowId: revShare.revenueShareDefinitionId,
              collapsibleRowContent: (
                <RevenueShareDetail
                  revenueShare={revShare}
                  handleEditRevenueShareClick={onEditRevenueShareClick}
                />
              ),
              columnStyles: {
                textTransform: 'capitalize',
              },
              // ACCOUNTS-AND-PLAN-MIGRATION: revShare.clientId/revShare.partnershipId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
              columnValues: showScopeColumns
                ? [
                    {
                      columnItem: revShare.clientId
                        ? 'Client Level'
                        : 'Partnership Level',
                    },
                    {
                      columnItem:
                        (revShare.partnershipId &&
                          partnershipMap?.get(revShare.partnershipId)) ||
                        '--',
                    },
                    {
                      columnItem:
                        (revShare.clientId &&
                          clientMap?.get(revShare.clientId)) ||
                        '--',
                    },
                    { columnItem: revShare.effectiveYear },
                    {
                      columnItem: (
                        <HHChip
                          hhVariant="variant-bypass"
                          color={calculateChipColor(revShare.status)}
                          size="small"
                          label={revShare.status}
                        />
                      ),
                    },
                  ]
                : [
                    { columnItem: 'Revenue Share' },
                    {
                      columnItem: (
                        <RevenueShareLevelDisplay revenueShare={revShare} />
                      ),
                    },
                    { columnItem: revShare.effectiveYear },
                    {
                      columnItem: (
                        <HHChip
                          hhVariant="variant-bypass"
                          color={calculateChipColor(revShare.status)}
                          size="small"
                          label={revShare.status}
                        />
                      ),
                    },
                  ],
            }))
          : []
      }
    />
  );
};

export default RevenueSharesList;
